import { VDBText } from "@components/atoms";
import CustomWeb from "@components/atoms/Webview";
import { useTypographyStore } from "@redux/slices/typographySlice";
import Footer from "@scenes/pdp/utils/Footer";
import { Diamond } from "@src/types/DiamondDetail";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DeviceEventEmitter, Platform, StyleSheet, View } from "react-native";
import styled, { useTheme } from "styled-components/native";
import EmptyImagePreview from "../GalleryView/utils/EmptyImage/EmptyImagePreview";
import { getShapeValue } from "../GalleryView/utils/EmptyImage/utils";
import { GalleryCategory } from "../GalleryView/utils/GalleryType";
import OpenReportView from "../GalleryView/utils/OpenReport/OpenReportView";
import GalleryIconsView from "../GalleryViewModel/GalleryIconsView";
import { Image as ExpoImage } from "expo-image";
import { isJPGUrl, isPdfUrl } from "../GalleryView/utils/helpers";
import {
  getImageSource,
  getIOSImageProps,
  getOptimizedImageUrl,
  ImageSizePreset,
} from "@utils/image/imageOptimizer";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useIsFoldable } from "@navigations/utils/FoldableWrapper";
import ShareModal from "../Share";
import { ShareType } from "../Share/types";
import { isPair } from "@utils/product/productValidators";

type GalleryViewModalProps = {
  isSmallDeviceWidth: boolean;
  resizeImage: boolean;
  ReceivedMedia: any;
  initialIndex?: number;
  image_thumb_url?: string;
  productType?: string;
  itemDetail?: Diamond;
  ceterIconTheme?: string;
  isOpen?: boolean;
  modalText?: string;
  fromMatchPair?: boolean;
  noImageBorderRadius?: boolean;
  selectedType?: string;
  onClose: () => void;
};

const ImageComponentView = ({
  isOpen,
  ReceivedMedia,
  initialIndex = 0,
  itemDetail,
  isSmallDeviceWidth,
  resizeImage,
  productType,
  fromMatchPair,
  modalText = "findMatch.matchStone.text",
  noImageBorderRadius = true,
  selectedType = "image",
  onClose,
}: GalleryViewModalProps) => {
  const theme = useTheme();
  const [activeIndex, setActiveIndex] = useState(initialIndex);

  useEffect(() => {
    setActiveIndex(0);
  }, [selectedType]);

  const typography = useTypographyStore();
  const { t } = useTranslation();

  const getLinksByType = (data: any, type: string) => {
    return data[type].map((item) => item);
  };

  const [mediaType, setMediaType] = useState(selectedType);
  const [mediaArray, setMediaArray] = useState(
    getLinksByType(ReceivedMedia, mediaType)
  );
  const [shareMenuOpen, setShareMenuOpen] = useState(false);
  const ImageComponent = ExpoImage;
  const [showShareModal, setshowShareModal] = useState(false);
  const [selectedOption, setSelectedOption] = useState("");

  useEffect(() => {
    if (selectedType) {
      setMediaType(selectedType);
      setMediaArray(getLinksByType(ReceivedMedia, selectedType));
    }
  }, [selectedType]);

  useEffect(() => {
    if (mediaArray?.length === 0) {
      setMediaArray([{ type: "image", link: "" }]);
    }
  }, [ReceivedMedia]);
  const { deviceWidth: screenWidth } = useIsFoldable();
  const [deviceWidth] = useState(screenWidth);
  let resizeMode = "contain";
  let applyRadius = false;

  let extraSmallImage = false;
  let isExternalView = GalleryCategory.ModelView;
  let showMediaModel = false;
  let isSmallVideoView = false;
  let GIACertEnable = true;

  const preFix =
    Platform.OS === "android"
      ? "https://docs.google.com/gview?embedded=true&url="
      : "";

  const loadingUrlExternalView =
    mediaArray[activeIndex]?.type === "pdf" &&
    (isPdfUrl(mediaArray[activeIndex].link) || showMediaModel)
      ? true
      : false;

  const [imageError, setImageError] = useState(
    mediaArray[activeIndex]?.link === "" ? true : false
  );

  useEffect(() => {
    if (mediaArray.length !== 1) {
      setActiveIndex(initialIndex);
    }
  }, [initialIndex]);

  useEffect(() => {
    const shareMenuSwitch = DeviceEventEmitter.addListener(
      "SHARE_MENU_SWITCH",
      () => {
        setShareMenuOpen(false);
      }
    );

    return () => {
      shareMenuSwitch.remove();
    };
  }, [shareMenuOpen]);
  const isJPGImage = isJPGUrl(mediaArray[activeIndex]?.link);

  const injectedCSS = `
  .default-view, .main {
    display: table;
    margin: 0;
  }
  .divbtnpos, .sliderHeight {
    display: none !important;
  }
  
  .frameContainer, .V360-instance {
      height: auto !important;
      aspect-ratio: 1 !important;
  }
`;
  const imageComponent = useMemo(() => {
    return mediaArray[activeIndex]?.type === "image" ||
      isJPGImage ||
      (mediaArray[activeIndex]?.type === "pdf" &&
        mediaArray[activeIndex]?.link === "") ||
      (mediaArray[activeIndex]?.type === "video" &&
        mediaArray[activeIndex]?.link === "") ? (
      !imageError ? (
        <ImageComponent
          contentFit={
            (mediaArray[activeIndex]?.link
              ? isJPGImage && mediaArray[activeIndex]?.type === "pdf"
                ? "contain"
                : resizeMode
              : "contain") as
              | "cover"
              | "contain"
              | "fill"
              | "none"
              | "scale-down"
          }
          source={getImageSource(
            getOptimizedImageUrl(
              mediaArray[activeIndex]?.link,
              ImageSizePreset.PDP_MAIN
            ),
            ImageSizePreset.PDP_MAIN
          )}
          {...getIOSImageProps()}
          onError={() => setImageError(true)}
          style={[
            isSmallDeviceWidth
              ? styles.smallimage(deviceWidth, applyRadius, resizeImage, false)
              : null,
            resizeImage
              ? !theme.isMobile
                ? styles.smallimage(600, applyRadius, resizeImage, true)
                : styles.galleryimage
              : extraSmallImage
              ? undefined
              : styles.largeimage,
          ]}
        />
      ) : (
        <EmptyImagePreview
          shape={
            mediaArray[activeIndex]?.type === "pdf"
              ? "pdf"
              : mediaArray[activeIndex]?.type === "video"
              ? "video"
              : getShapeValue(itemDetail)
          }
          size={extraSmallImage ? 100 : 150}
          extraSmallImage={extraSmallImage}
          isExternalView={isExternalView}
          isPair={isPair(itemDetail) ?? false}
        />
      )
    ) : loadingUrlExternalView ? (
      <OpenReportView
        resizeImage={extraSmallImage}
        url={mediaArray[activeIndex].link}
        showMediaModel={showMediaModel}
        mediaType={mediaArray[activeIndex].type}
        itemID={itemDetail?.id}
        certNum={itemDetail?.cert_num}
        GIACertEnable={GIACertEnable}
        linkedPairChildren={itemDetail?.children}
      />
    ) : (
      <Container>
        <View
          style={{
            width:
              isSmallVideoView && mediaArray[activeIndex].type === "video"
                ? "75%"
                : fromMatchPair
                ? theme.isMobile
                  ? deviceWidth / 1.9
                  : 400
                : "100%",
            height: "100%",
          }}
        >
          <CustomWeb
            isSmallDeviceWidth={isSmallDeviceWidth}
            deviceWidth={deviceWidth}
            resizeImage={resizeImage}
            url={
              mediaArray[activeIndex]?.type === "pdf"
                ? preFix + mediaArray[activeIndex]?.link
                : mediaArray[activeIndex]?.link
            }
            isFromPDP={false}
            extraSmallImage={extraSmallImage}
            enableReload={mediaArray[activeIndex]?.type === "pdf"}
            allowScaleScript={false}
            showLoader={true}
            width={theme.isMobile ? deviceWidth / 1.9 : undefined}
            mediaType={mediaArray[activeIndex]?.type}
            videoType={mediaArray[activeIndex]?.video_type}
            injectedCSS={injectedCSS}
          />
        </View>
      </Container>
    );
  }, [
    mediaArray[activeIndex]?.link,
    mediaArray[activeIndex]?.type,
    resizeMode,
    imageError,
    setImageError,
  ]);

  const bottomLeftItemLeftClickEvent = () => {
    if (activeIndex !== 0) {
      setActiveIndex(activeIndex - 1);
    }
  };

  const bottomRightItemRightClickEvent = () => {
    if (activeIndex < mediaArray?.length - 1) {
      setActiveIndex(activeIndex + 1);
    }
  };

  const checkHideCondition = () => {
    return (
      mediaArray.length >= 1 && mediaArray?.every((item) => item.link === "")
    );
  };

  const FooterComponent = () => {
    return (
      <PriceDetailContainer deviceWidth={deviceWidth - 34} isOpen={isOpen}>
        <FlexMainView>
          {theme.isMobile && (
            <MainContainerView>
              <VDBText color={theme["core-05"]} type={typography["H3-M"]}>
                {t(modalText)}
              </VDBText>
            </MainContainerView>
          )}

          <MainContainerView>
            {checkHideCondition() ? (
              <IconExtraView />
            ) : (
              <Footer
                hasArrow={true}
                centerString={"of"}
                total={mediaArray.length}
                currentIndex={activeIndex}
                leftClickEvent={bottomLeftItemLeftClickEvent}
                rightClickEvent={bottomRightItemRightClickEvent}
              />
            )}
          </MainContainerView>
        </FlexMainView>
      </PriceDetailContainer>
    );
  };

  return (
    <ScrollContainer>
      <MainContainer
        isOpen={isOpen}
        deviceWidth={deviceWidth}
        isSmallDeviceWidth={isSmallDeviceWidth}
        noImageBorderRadius={noImageBorderRadius}
      >
        {imageComponent}
      </MainContainer>
      <ThumbContainer>
        <GalleryIconsView
          isOpen={isOpen}
          productType={productType}
          itemDetail={itemDetail}
          mediaUrl={mediaArray[activeIndex]?.link}
          fromMatchPair={fromMatchPair}
          closeViewModal={onClose}
          shareMenuOpen={shareMenuOpen}
          setShareMenuOpen={setShareMenuOpen}
          setshowShareModal={setshowShareModal}
          setSelectedOption={setSelectedOption}
          isJewelryComponent={false}
        />
      </ThumbContainer>

      <CounterContainer>
        <FooterComponent />
      </CounterContainer>

      {showShareModal && (
        <ShareModal
          isOpen={showShareModal}
          onClose={() => setshowShareModal(false)}
          item={itemDetail}
          shareType={ShareType.singleShare}
          selectedOption={selectedOption}
        />
      )}
    </ScrollContainer>
  );
};

const MainContainerView = styled.View<any>`
  item-content: center;
  item-self: center;
`;

const styles = StyleSheet.create({
  containerStyle: {
    height: "100%",
    width: "100%",
  },
  centercontainerStyle: {
    height: "100%",
    width: "100%",
    alignItems: "center",
  },
  largeimage: {
    height: "100%",
    width: "100%",
  },
  tabletContainerStyle: {
    height: "100%",
    width: "100%",
  },
  galleryimage: {
    justifyContent: "center",
    alignItems: "center",
  },
  smallimage: (
    deviceWidth: number,
    applyRadius: boolean,
    resizeImage: boolean,
    applyPercent: boolean
  ) => ({
    height: applyPercent
      ? "100%"
      : resizeImage
      ? deviceWidth / 1.9
      : deviceWidth,
    width: applyPercent
      ? "100%"
      : resizeImage
      ? deviceWidth / 1.9
      : deviceWidth,
    borderRadius: applyRadius ? 12 : 0,
    paddingBottom: "20px",
    overflow: "hidden",
  }),
  header: {
    position: "absolute",
    top: 0,
    width: "100%",
  },
  footer: {
    bottom: 0,
    position: "absolute",
    width: "100%",
  },
  activeThumb: {
    borderWidth: 3,
  },
  thumb: {
    borderRadius: 8,
    marginRight: 10,
    borderWidth: 1,
  },
  thumbnailListContainer: {
    paddingHorizontal: 10,
  },
  bottomFlatlist: {
    position: "absolute",
  },
});
const MainContainer = styled.View<any>`
  height: ${({ isOpen, theme, deviceWidth }) =>
    isOpen && !theme.isMobile ? "400px" : deviceWidth / 1.9};

  item-content: center;
  align-items: center;
  border-radius: ${({ noImageBorderRadius }) =>
    noImageBorderRadius ? 0 : 8}px;
  overflow: hidden;
`;

const ScrollContainer = styled.View``;

const Container = styled.View`
  flex: 1;
  flex-basis: auto;
`;

const ThumbContainer = styled.View`
  position: absolute;
  flex: 1;
  flex-basis: auto;
  width: 100%;
`;

const CounterContainer = styled.View`
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  background-color: ${({ theme }) => theme["core-03"]};
`;

const FlexMainView = styled.View<any>`
  flex-direction: row;
  justify-content: ${({ theme }) =>
    theme.isMobile ? "space-between" : "center"};
  item-content: center;
  align-items: center;
  margin-left: 10px;
  margin-right: 10px;
`;

const PriceDetailContainer = styled.View<any>`
  flex-direction: column;
  width: ${({ deviceWidth, isOpen, theme }) =>
    isOpen && !theme.isMobile ? 400 : deviceWidth};
  padding-top: ${SpaceType.Basic}px;
  padding-bottom: ${SpaceType.Basic}px;
  border-top-width: 1px;
  border-top-color: ${({ theme }) => theme["core-04"]};
`;
const IconExtraView = styled.View`
  padding: 8px;
`;
export default ImageComponentView;
